import { useState } from "react";
import { Scale, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const QUESTIONS = [
  ["can_supply", "Can PGPS supply the requirement?"],
  ["meets_naics", "Do we meet the NAICS/category?"],
  ["meets_qualifications", "Do we meet required qualifications?"],
  ["can_meet_delivery", "Can we meet delivery?"],
  ["competitive_pricing", "Can we obtain competitive supplier pricing?"],
  ["profitable", "Is the opportunity profitable?"],
  ["certifications_available", "Are required certifications available?"],
  ["subcontracting_ok", "Are there subcontracting/partner requirements we can meet?"],
  ["compliance_ok", "Can we meet mandatory clauses / compliance requirements?"],
  ["deadline_achievable", "Is the deadline achievable?"],
];
const DECISIONS = ["GO", "NO_GO", "NEEDS_REVIEW"];
export const DECISION_BADGE = {
  GO: "bg-green-100 text-green-700", NO_GO: "bg-red-100 text-red-600", NEEDS_REVIEW: "bg-amber-100 text-amber-700",
};

export function BidNoBid({ opp, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const existing = opp.bid_analysis || {};
  const [answers, setAnswers] = useState(existing.answers || {});
  const [decision, setDecision] = useState(existing.decision || "NEEDS_REVIEW");
  const [notes, setNotes] = useState(existing.notes || "");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/gov-opps/${opp.id}/bid-analysis`, { answers, decision, notes });
      toast.success(`Bid/No-Bid saved: ${decision.replace("_", "-")}`);
      setOpen(false);
      onSaved();
    } catch { toast.error("Could not save bid analysis"); } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid={`gov-bid-analysis-${opp.id}`} onClick={() => setOpen(true)} title="Bid / No-Bid analysis"
        className={`inline-flex items-center gap-1 text-[10.5px] font-[Poppins] font-bold px-2 py-1 rounded-full ${existing.decision ? DECISION_BADGE[existing.decision] : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
        <Scale size={11} /> {existing.decision ? existing.decision.replace("_", "-") : "Bid/No-Bid"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto" data-testid="bid-analysis-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Poppins] text-[var(--pg-navy)]">Bid / No-Bid Analysis</DialogTitle>
            <DialogDescription>{opp.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {QUESTIONS.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <span className="text-[13px] text-[var(--pg-navy)]">{label}</span>
                <select data-testid={`bid-q-${key}`} className="pg-input !py-1.5 !px-2 !w-[110px] text-[12.5px]"
                  value={answers[key] || "UNKNOWN"} onChange={(e) => setAnswers((a) => ({ ...a, [key]: e.target.value }))}>
                  <option value="YES">Yes</option><option value="NO">No</option><option value="UNKNOWN">Unknown</option>
                </select>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-100">
              <label className="pg-label">Decision</label>
              <div className="flex gap-2 mt-1">
                {DECISIONS.map((d) => (
                  <button key={d} data-testid={`bid-decision-${d}`} onClick={() => setDecision(d)}
                    className={`px-3.5 py-2 rounded-lg text-[12.5px] font-[Poppins] font-bold border transition-colors ${decision === d ? DECISION_BADGE[d] + " border-transparent ring-2 ring-offset-1 ring-[var(--pg-accent)]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                    {d.replace("_", "-")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="pg-label">Notes</label>
              <textarea data-testid="bid-notes-input" rows={2} className="pg-input !py-2" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <button data-testid="bid-save-button" onClick={save} disabled={busy}
              className="btn-primary w-full !py-3 text-[13px] justify-center disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Save Analysis"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
